import { createFileRoute, Link } from '@tanstack/react-router'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { QueryGuard } from '@/components/query-guard'
import { AdminCreditsSection } from '@/features/admin/components/admin-credits-section'
import { AdminPersonaSection } from '@/features/admin/components/admin-persona-section'
import { InvestorView } from '@/features/investor/components/investor-view'
import {
  useAdminAddInvestorCreditsMutation,
  useAdminInvestorByIdQuery,
  useAdminInvestorCreditsQuery,
  useAdminInvestorPersonaQuery,
} from '@/hooks/api'

export const Route = createFileRoute('/_protected/admin/investors/$id')({
  component: AdminInvestorDetailPage,
})

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'persona', label: 'Persona' },
  { key: 'credits', label: 'Credits' },
] as const

type Tab = (typeof tabs)[number]['key']

function AdminInvestorDetailPage() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [pollEnabled, setPollEnabled] = useState(false)
  const investorQuery = useAdminInvestorByIdQuery(id)
  const creditsQuery = useAdminInvestorCreditsQuery(id)
  const personaQuery = useAdminInvestorPersonaQuery(id, {
    refetchInterval: pollEnabled ? 5000 : false,
  })
  const addCreditsMutation = useAdminAddInvestorCreditsMutation()

  useEffect(() => {
    const hasActive =
      personaQuery.data?.data.researchRequests?.some(
        (r) => r.status === 'PENDING' || r.status === 'ACTIVE'
      ) ?? false
    setPollEnabled(hasActive)
  }, [personaQuery.data])

  const handleAddCredits = async (amount: number, note?: string) => {
    await addCreditsMutation.mutateAsync({
      path: { id },
      body: { amount, note },
    })
  }

  return (
    <QueryGuard query={investorQuery} errorMessage="Failed to load investor">
      {(investor) => (
        <>
          <nav className="border-b border-border">
            <div className="flex items-center gap-2 py-3">
              <Link
                to="/admin/investors"
                className="flex items-center gap-1 text-sm font-medium text-orange-500 hover:underline"
              >
                <ArrowLeft className="size-4" />
                Investors
              </Link>
              <ChevronRight className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">{investor.name}</span>
            </div>
            <div className="flex gap-1 -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    activeTab === tab.key
                      ? 'border-orange-500 text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
          <div className="py-6">
            {activeTab === 'overview' && <InvestorView investor={investor} canEdit={false} />}
            {activeTab === 'persona' && (
              <AdminPersonaSection
                investorId={id}
                personas={personaQuery.data?.data.personas}
                researchRequests={personaQuery.data?.data.researchRequests}
                isLoading={personaQuery.isLoading}
              />
            )}
            {activeTab === 'credits' && (
              <AdminCreditsSection
                balance={creditsQuery.data?.data.balance}
                transactions={creditsQuery.data?.data.transactions}
                isLoading={creditsQuery.isLoading}
                onAddCredits={handleAddCredits}
                isAdding={addCreditsMutation.isPending}
              />
            )}
          </div>
        </>
      )}
    </QueryGuard>
  )
}
